import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useJobsite } from "../context/JobsiteContext";
import Sheet from "../components/Sheet";
import { compressImage } from "../lib/image";
import { getPosition } from "../lib/geo";
import { fmtSize, openDocument, resolveFileUrl } from "../lib/openDocument";
import {
  Camera,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FolderPlus,
  ImagePlus,
  Images,
  Info,
  Loader2,
  MapPin,
  MoreHorizontal,
  Pencil,
  Search,
  Share2,
  Trash2,
  User,
  X,
} from "lucide-react";

/* ---------- datas (no horario do aparelho) ---------- */

const pad = (n) => String(n).padStart(2, "0");
const dayKeyOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const shortDay = (d) => `${d.toLocaleDateString("en-AU", { weekday: "short" })} ${d.getDate()} ${d.toLocaleDateString("en-AU", { month: "short" })}`;
const timeLabel = (d) => d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });

function dayLabel(d) {
  const t = new Date();
  const y = new Date(t.getFullYear(), t.getMonth(), t.getDate() - 1);
  if (dayKeyOf(d) === dayKeyOf(t)) return `Today · ${shortDay(d)}`;
  if (dayKeyOf(d) === dayKeyOf(y)) return `Yesterday · ${shortDay(d)}`;
  return shortDay(d);
}

function whenLabel(iso) {
  if (!iso) return "No photos yet";
  const d = new Date(iso);
  const t = new Date();
  const y = new Date(t.getFullYear(), t.getMonth(), t.getDate() - 1);
  if (dayKeyOf(d) === dayKeyOf(t)) return `today ${timeLabel(d)}`;
  if (dayKeyOf(d) === dayKeyOf(y)) return "yesterday";
  return shortDay(d);
}

const uid = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const DAY = 24 * 3600 * 1000;

/* ---------- pecas pequenas ---------- */

function TopBar({ title, onBack, right }) {
  return (
    <div className="mb-3 flex items-center gap-1">
      <button type="button" onClick={onBack} aria-label="Back" className="grid h-11 w-11 shrink-0 place-items-center rounded-full active:bg-accent" data-testid="back">
        <ChevronLeft className="h-7 w-7" />
      </button>
      <h1 className="min-w-0 flex-1 truncate text-center text-lg font-bold" data-testid="page-title">
        {title}
      </h1>
      <div className="grid h-11 w-11 shrink-0 place-items-center">{right}</div>
    </div>
  );
}

function Mosaic({ urls }) {
  return (
    <div className="grid h-14 w-14 shrink-0 grid-cols-2 gap-0.5 overflow-hidden rounded-xl bg-muted" data-testid="mosaic">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="bg-card">
          {urls[i] ? <img src={resolveFileUrl(urls[i])} alt="" loading="lazy" className="h-full w-full object-cover" /> : null}
        </div>
      ))}
    </div>
  );
}

function BigButton({ children, onClick, testId, outline = false, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex h-14 w-full items-center justify-center gap-2.5 rounded-2xl text-base font-bold transition-colors disabled:opacity-50 ${
        outline ? "border border-border bg-card active:bg-accent" : "bg-primary text-primary-foreground active:opacity-90"
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

const selectClass = "h-12 w-full appearance-none rounded-xl border border-input bg-background px-3 pr-10 text-base font-semibold outline-none focus:border-primary disabled:opacity-60";

/* ---------- janela: nova area ---------- */

function NewAreaSheet({ site, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const save = async () => {
    if (busy || !name.trim()) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/photo-areas", { name, jobsite_id: site.id });
      toast.success("Folder created");
      onCreated(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, "Could not create the folder."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet
      title="New folder"
      subtitle={`In ${site.name}`}
      onClose={onClose}
      footer={
        <BigButton onClick={save} disabled={busy || !name.trim()} testId="new-area-save">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Create folder
        </BigButton>
      }
    >
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted-foreground">Folder name</label>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && save()}
        placeholder="e.g. Level 5 podium combiflex"
        maxLength={80}
        autoFocus
        className="h-12 w-full rounded-xl border border-input bg-background px-3 text-base outline-none focus:border-primary"
        data-testid="new-area-input"
      />
      <p className="mt-2 text-xs text-muted-foreground">Use the place where the work happens, so the crew can find it fast.</p>
      {error && (
        <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400" role="alert" data-testid="new-area-error">
          {error}
        </div>
      )}
    </Sheet>
  );
}

/* ---------- janela: adicionar fotos ---------- */

function AddPhotosSheet({ site, areasProp, initialAreaId, onClose, onDone }) {
  const [areas, setAreas] = useState(areasProp || null);
  const [areaId, setAreaId] = useState(initialAreaId || "");
  const [items, setItems] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const camRef = useRef(null);
  const libRef = useRef(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    if (areasProp) return undefined;
    let cancelled = false;
    api
      .get("/photo-areas", { params: { jobsite_id: site.id } })
      .then(({ data }) => {
        if (!cancelled) setAreas(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setAreas([]);
      });
    return () => {
      cancelled = true;
    };
  }, [areasProp, site.id]);

  // libera as miniaturas da memoria ao fechar
  useEffect(
    () => () => {
      itemsRef.current.forEach((i) => URL.revokeObjectURL(i.preview));
    },
    []
  );

  const addFiles = (list) => {
    const files = Array.from(list || []).filter((f) => (f.type && f.type.startsWith("image/")) || /heic|heif/i.test(f.name || ""));
    if (files.length === 0) return;
    setItems((prev) => {
      const room = Math.max(0, 20 - prev.length);
      if (files.length > room) toast.info("You can add up to 20 photos at a time.");
      return [...prev, ...files.slice(0, room).map((file) => ({ key: uid(), file, preview: URL.createObjectURL(file), status: "ready", error: "" }))];
    });
  };
  const onPick = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };
  const remove = (key) => {
    setItems((prev) => {
      const gone = prev.find((i) => i.key === key);
      if (gone) URL.revokeObjectURL(gone.preview);
      return prev.filter((i) => i.key !== key);
    });
  };

  const upload = async () => {
    const todo = items.filter((i) => i.status !== "done");
    if (busy || !areaId || todo.length === 0) return;
    setBusy(true);
    let pos = null;
    try {
      pos = await getPosition(4000);
    } catch (e) {
      pos = null; // sem localizacao: a foto vai assim mesmo
    }
    let sent = 0;
    let failed = 0;
    for (const it of todo) {
      setItems((prev) => prev.map((x) => (x.key === it.key ? { ...x, status: "uploading", error: "" } : x)));
      try {
        const full = await compressImage(it.file, 1600, 0.8);
        const small = await compressImage(it.file, 360, 0.7);
        const fd = new FormData();
        fd.append("file", full, full.name || "photo.jpg");
        fd.append("thumb", small, small.name || "thumb.jpg");
        fd.append("note", note);
        const taken = new Date(it.file.lastModified || Date.now());
        const areaLabel = ((areas || []).find((a) => a.id === areaId) || {}).name || "Photo";
        fd.append("title", `${areaLabel} · ${shortDay(taken)} ${timeLabel(taken)}`);
        fd.append("taken_at", new Date(it.file.lastModified || Date.now()).toISOString());
        fd.append("client_id", it.key);
        if (pos) {
          fd.append("lat", String(pos.lat));
          fd.append("lng", String(pos.lng));
          if (pos.accuracy != null) fd.append("accuracy", String(pos.accuracy));
        }
        await api.post(`/photo-areas/${areaId}/photos`, fd, { timeout: 120000 });
        sent += 1;
        setItems((prev) => prev.map((x) => (x.key === it.key ? { ...x, status: "done" } : x)));
      } catch (e) {
        failed += 1;
        const msg = formatApiError(e.response?.data?.detail, "Could not send");
        setItems((prev) => prev.map((x) => (x.key === it.key ? { ...x, status: "error", error: msg } : x)));
      }
    }
    setBusy(false);
    if (sent > 0) onDone(areaId);
    if (failed === 0) {
      toast.success(`${plural(sent, "photo", "photos")} added`);
      onClose();
    } else {
      toast.error(`${plural(failed, "photo", "photos")} did not upload. Check your signal and tap Retry.`);
    }
  };

  const pending = items.filter((i) => i.status !== "done").length;
  const failedCount = items.filter((i) => i.status === "error").length;
  const recent = (areas || []).filter((a) => a.id !== areaId).slice(0, 3);
  const noAreas = areas && areas.length === 0;

  return (
    <Sheet
      title="Add photos"
      subtitle={site.name}
      onClose={busy ? () => {} : onClose}
      footer={
        <BigButton onClick={upload} disabled={busy || !areaId || pending === 0} testId="add-submit">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
          {busy ? "Uploading..." : failedCount > 0 ? `Retry ${failedCount}` : pending > 0 ? `Upload ${plural(pending, "photo", "photos")}` : "Upload"}
        </BigButton>
      }
    >
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted-foreground" htmlFor="add-area">
        Folder (required)
      </label>
      {noAreas ? (
        <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground" data-testid="add-no-areas">
          There are no folders yet. Ask your supervisor to create one.
        </div>
      ) : (
        <>
          <div className="relative">
            <select id="add-area" value={areaId} onChange={(e) => setAreaId(e.target.value)} disabled={busy || areas === null} className={selectClass} data-testid="add-area-select">
              <option value="">{areas === null ? "Loading..." : "Choose the folder"}</option>
              {(areas || []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          </div>
          {recent.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2" data-testid="add-recent">
              {recent.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  disabled={busy}
                  onClick={() => setAreaId(a.id)}
                  className="max-w-full truncate rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground active:bg-accent"
                  data-testid={`add-recent-${a.id}`}
                >
                  {a.name}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      <div className="mb-1.5 mt-5 text-xs font-bold uppercase tracking-wide text-muted-foreground">Photos{items.length ? ` · ${items.length}` : ""}</div>
      <div className="flex flex-wrap gap-2" data-testid="add-items">
        {items.map((it, n) => (
          <div key={it.key} className="relative h-[72px] w-[72px] overflow-hidden rounded-xl border border-border bg-muted" data-testid={`add-item-${n}`} data-status={it.status}>
            <img src={it.preview} alt="" className={`h-full w-full object-cover ${it.status === "done" ? "opacity-50" : ""}`} />
            {it.status === "uploading" && (
              <div className="absolute inset-0 grid place-items-center bg-black/50">
                <Loader2 className="h-5 w-5 animate-spin text-white" />
              </div>
            )}
            {it.status === "done" && <div className="absolute inset-x-0 bottom-0 bg-black/60 py-0.5 text-center text-[10px] font-bold text-white">Sent</div>}
            {it.status === "error" && (
              <div className="absolute inset-x-0 bottom-0 bg-red-600/90 py-0.5 text-center text-[10px] font-bold text-white" title={it.error}>
                Failed
              </div>
            )}
            {!busy && it.status !== "done" && (
              <button type="button" onClick={() => remove(it.key)} aria-label="Remove photo" className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/70 text-white" data-testid={`add-remove-${n}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          disabled={busy}
          onClick={() => camRef.current && camRef.current.click()}
          aria-label="Take a photo"
          className="grid h-[72px] w-[72px] place-items-center rounded-xl border border-dashed border-input text-muted-foreground active:bg-accent disabled:opacity-50"
          data-testid="add-take"
        >
          <Camera className="h-6 w-6" />
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => libRef.current && libRef.current.click()}
          aria-label="Choose from your photos"
          className="grid h-[72px] w-[72px] place-items-center rounded-xl border border-dashed border-input text-muted-foreground active:bg-accent disabled:opacity-50"
          data-testid="add-library"
        >
          <ImagePlus className="h-6 w-6" />
        </button>
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid="add-camera-input" />
      <input ref={libRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="add-library-input" />
      {items.some((i) => i.status === "error") && (
        <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400" role="alert" data-testid="add-error">
          {items.find((i) => i.status === "error").error}
        </div>
      )}

      <label className="mb-1.5 mt-5 block text-xs font-bold uppercase tracking-wide text-muted-foreground" htmlFor="add-note">
        Note (optional)
      </label>
      <textarea
        id="add-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        maxLength={500}
        disabled={busy}
        placeholder="e.g. Primer applied, second coat tomorrow"
        className="w-full resize-none rounded-xl border border-input bg-background px-3 py-3 text-base outline-none focus:border-primary disabled:opacity-60"
        data-testid="add-note"
      />
      <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
        <MapPin className="h-3.5 w-3.5 shrink-0" /> Time and GPS are added automatically.
      </p>
    </Sheet>
  );
}

/* ---------- lista de areas ---------- */

function AreasView({ site, isStaff }) {
  const navigate = useNavigate();
  const [areas, setAreas] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/photo-areas", { params: { jobsite_id: site.id } });
      setAreas(Array.isArray(data) ? data : []);
      setError("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, "Could not load the folders."));
      setAreas((a) => a || []);
    }
  }, [site.id]);

  useEffect(() => {
    setAreas(null);
    load();
  }, [load]);

  const q = query.trim().toLowerCase();
  const shown = (areas || []).filter((a) => !q || a.name.toLowerCase().includes(q));

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 md:px-6 md:pb-12 md:pt-8" data-testid="photos-page">
      <TopBar title="Documents" onBack={() => navigate("/app")} />
      <div className="relative">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search folders"
          className="h-12 w-full rounded-xl border border-input bg-card pl-11 pr-3 text-base outline-none focus:border-primary"
          data-testid="area-search"
        />
      </div>
      <div className="mt-3 space-y-2.5">
        <BigButton onClick={() => setAdding(true)} testId="add-photos">
          <Camera className="h-5 w-5" /> Add photos
        </BigButton>
        {isStaff && (
          <BigButton onClick={() => setCreating(true)} outline testId="new-area">
            <FolderPlus className="h-5 w-5" /> New folder
          </BigButton>
        )}
      </div>

      {error && (
        <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400" role="alert" data-testid="areas-error">
          {error}
        </div>
      )}

      {areas === null ? (
        <div className="grid h-40 place-items-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : areas.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border p-8 text-center" data-testid="areas-empty">
          <Images className="mx-auto h-8 w-8 text-muted-foreground" />
          <div className="mt-3 text-lg font-bold">No folders yet</div>
          <div className="mt-1 text-sm text-muted-foreground">
            {isStaff ? "Create the first folder, for example “Level 5 podium”. The crew adds photos to it." : "Ask your supervisor to create the folders where you work."}
          </div>
        </div>
      ) : (
        <>
          <div className="mx-0.5 mb-1 mt-4 flex items-center justify-between text-xs text-muted-foreground">
            <span data-testid="areas-count">{plural(shown.length, "folder", "folders")}</span>
            <span>Latest photo first</span>
          </div>
          <div data-testid="areas-list">
            {shown.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">No folder matches your search.</div>}
            {shown.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => navigate(`/app/photos/${a.id}`)}
                className="flex min-h-[72px] w-full items-center gap-3.5 border-b border-border py-3 text-left active:bg-accent/50"
                data-testid={`area-row-${a.id}`}
              >
                <Mosaic urls={a.previews || []} />
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold leading-snug">{a.name}</div>
                  <div className="mt-0.5 text-[13px] text-muted-foreground">
                    {a.photo_count > 0 ? `${plural(a.photo_count, "photo", "photos")} · ${whenLabel(a.last_photo_at)}` : "No photos yet"}
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        </>
      )}

      {adding && <AddPhotosSheet site={site} areasProp={areas || undefined} initialAreaId="" onClose={() => setAdding(false)} onDone={() => load()} />}
      {creating && (
        <NewAreaSheet
          site={site}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}

/* ---------- foto aberta ---------- */

// [photo-menu] pecas da foto aberta (menu, informacoes e edicao)
function safeFileName(s) {
  const cleaned = String(s || "")
    .split("")
    .map((c) => (/[A-Za-z0-9 ._-]/.test(c) ? c : " "))
    .join("")
    .split(" ")
    .filter(Boolean)
    .join(" ")
    .slice(0, 80);
  return cleaned || "photo";
}

function Panel({ title, onClose, children }) {
  return (
    <div className="absolute inset-0 z-40 flex items-end bg-black/60" data-testid="viewer-panel">
      <button type="button" aria-label="Close" className="absolute inset-0 cursor-default" onClick={onClose} />
      <div className="relative max-h-[88%] w-full overflow-y-auto rounded-t-3xl border-t border-white/10 bg-zinc-900 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold" data-testid="panel-title">
            {title}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="grid h-10 w-10 place-items-center rounded-full bg-white/10" data-testid="panel-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function InfoRow({ label, value, testId }) {
  return (
    <div className="border-t border-white/10 py-3" data-testid={testId}>
      <div className="text-xs font-bold uppercase tracking-wide text-white/60">{label}</div>
      <div className="mt-0.5 break-words text-[15px]">{value}</div>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testId, danger = false }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`flex h-12 w-full items-center justify-between gap-3 border-b border-white/10 px-4 text-left text-base last:border-0 active:bg-white/10 ${danger ? "text-red-400" : ""}`}
      data-testid={testId}
    >
      {label}
      <Icon className="h-5 w-5 shrink-0" />
    </button>
  );
}

function Viewer({ photos, index, areaName, onIndex, onClose, canDelete, onDelete, canEdit, onEdit }) {
  const p = photos[index];
  const touch = useRef(null);
  const [menu, setMenu] = useState(false);
  const [panel, setPanel] = useState("");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const go = useCallback(
    (n) => {
      const ni = index + n;
      if (ni >= 0 && ni < photos.length) onIndex(ni);
    },
    [index, photos.length, onIndex]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (panel) setPanel("");
        else if (menu) setMenu(false);
        else onClose();
      }
      if (panel || menu) return;
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [go, onClose, panel, menu]);

  if (!p) return null;
  const when = new Date(p.taken_at);
  const name = p.title || areaName;
  const gps = p.lat != null && p.lng != null ? `GPS${p.accuracy != null ? ` ±${Math.round(p.accuracy)} m` : ""}` : "";

  const start = (e) => {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY };
  };
  const end = (e) => {
    const s = touch.current;
    touch.current = null;
    if (!s) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx < 0 ? 1 : -1);
  };

  // Share: abre a janela de compartilhar do iPhone com a foto. Se o aparelho nao deixar, abre no visualizador do sistema.
  const share = async () => {
    setMenu(false);
    try {
      if (navigator.share && navigator.canShare) {
        const res = await fetch(resolveFileUrl(p.url));
        const blob = await res.blob();
        const ext = blob.type === "image/png" ? "png" : blob.type === "image/webp" ? "webp" : "jpg";
        const file = new File([blob], `${safeFileName(name)}.${ext}`, { type: blob.type || "image/jpeg" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: name, text: `${areaName} · ${shortDay(when)}` });
          return;
        }
      }
    } catch (e) {
      if (e && e.name === "AbortError") return; // a pessoa fechou a janela de compartilhar
    }
    openDocument(p.url);
  };

  const openEdit = () => {
    setMenu(false);
    setTitle(p.title || "");
    setNote(p.note || "");
    setEditError("");
    setPanel("edit");
  };
  const saveEdit = async () => {
    if (saving || !title.trim()) return;
    setSaving(true);
    setEditError("");
    try {
      await onEdit(p, { title, note });
      setPanel("");
      toast.success("Photo updated");
    } catch (e) {
      setEditError(formatApiError(e.response?.data?.detail, "Could not save the changes."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-black text-white" role="dialog" aria-modal="true" aria-label="Photo" data-testid="viewer">
      <div className="flex items-center justify-between px-2 pb-1 pt-[max(0.5rem,env(safe-area-inset-top))]">
        <button type="button" onClick={onClose} aria-label="Close" className="grid h-11 w-11 place-items-center rounded-full active:bg-white/10" data-testid="viewer-close">
          <X className="h-6 w-6" />
        </button>
        <div className="text-sm text-white/70" data-testid="viewer-count">
          {index + 1} / {photos.length}
        </div>
        <button type="button" onClick={() => setMenu((v) => !v)} aria-label="More options" aria-expanded={menu} className="grid h-11 w-11 place-items-center rounded-full active:bg-white/10" data-testid="viewer-menu-btn">
          <MoreHorizontal className="h-6 w-6" />
        </button>
      </div>

      <div className="relative min-h-0 flex-1" onTouchStart={start} onTouchEnd={end}>
        <img key={p.id} src={resolveFileUrl(p.url)} alt={p.note || name} className="absolute inset-0 h-full w-full object-contain" data-testid="viewer-img" />
        {index > 0 && (
          <button type="button" onClick={() => go(-1)} aria-label="Previous photo" className="absolute left-2 top-1/2 hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-black/60 md:grid" data-testid="viewer-prev">
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        {index < photos.length - 1 && (
          <button type="button" onClick={() => go(1)} aria-label="Next photo" className="absolute right-2 top-1/2 hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-black/60 md:grid" data-testid="viewer-next">
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>

      <div className="rounded-t-3xl border-t border-white/10 bg-zinc-900 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4" data-testid="viewer-info">
        <div className="text-lg font-bold leading-snug" data-testid="viewer-name">
          {name}
        </div>
        {p.title ? <div className="text-sm text-white/60">{areaName}</div> : null}
        <div className="mt-1.5 flex items-center gap-2 text-sm text-white/70">
          <User className="h-4 w-4 shrink-0" />
          <span>
            {p.user_name || "Unknown"} · {shortDay(when)}, {timeLabel(when)}
          </span>
        </div>
        {gps && (
          <div className="mt-1 flex items-center gap-2 text-sm text-white/70" data-testid="viewer-gps">
            <MapPin className="h-4 w-4 shrink-0" />
            <span>{gps}</span>
          </div>
        )}
        {p.note ? (
          <div className="mt-3 border-t border-white/10 pt-3 text-[15px] leading-snug" data-testid="viewer-note">
            {p.note}
          </div>
        ) : null}
      </div>

      {menu && (
        <>
          <button type="button" aria-label="Close menu" className="absolute inset-0 z-20 cursor-default" onClick={() => setMenu(false)} data-testid="viewer-menu-shade" />
          <div className="absolute right-2 top-[calc(max(0.5rem,env(safe-area-inset-top))+3.25rem)] z-30 w-64 overflow-hidden rounded-2xl border border-white/10 bg-zinc-800 shadow-2xl" role="menu" data-testid="viewer-menu">
            <MenuItem icon={Share2} label="Share" onClick={share} testId="menu-share" />
            <MenuItem
              icon={Info}
              label="Information"
              onClick={() => {
                setMenu(false);
                setPanel("info");
              }}
              testId="menu-info"
            />
            {canEdit(p) && <MenuItem icon={Pencil} label="Edit" onClick={openEdit} testId="menu-edit" />}
            <MenuItem
              icon={Download}
              label="Open original"
              onClick={() => {
                setMenu(false);
                openDocument(p.url);
              }}
              testId="menu-open"
            />
            {canDelete(p) && (
              <MenuItem
                icon={Trash2}
                label="Delete"
                danger
                onClick={() => {
                  setMenu(false);
                  onDelete(p);
                }}
                testId="menu-delete"
              />
            )}
          </div>
        </>
      )}

      {panel === "info" && (
        <Panel title="Information" onClose={() => setPanel("")}>
          <InfoRow label="Title" value={name} testId="info-title" />
          <InfoRow label="Added" value={`${shortDay(when)}, ${timeLabel(when)} by ${p.user_name || "Unknown"}`} testId="info-added" />
          {p.updated_at ? <InfoRow label="Edited" value={`${shortDay(new Date(p.updated_at))}, ${timeLabel(new Date(p.updated_at))}${p.updated_by ? ` by ${p.updated_by}` : ""}`} testId="info-edited" /> : null}
          <InfoRow label="Folder" value={areaName} testId="info-area" />
          <InfoRow label="Note" value={p.note || "--"} testId="info-note" />
          <InfoRow label="Location" value={gps || "--"} testId="info-gps" />
          <InfoRow label="File type" value={p.content_type ? `Image (${p.content_type.replace("image/", "").toUpperCase()})` : "Image"} testId="info-type" />
          <InfoRow label="File size" value={p.size ? fmtSize(p.size) : "--"} testId="info-size" />
        </Panel>
      )}

      {panel === "edit" && (
        <Panel title="Edit photo" onClose={() => (saving ? null : setPanel(""))}>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-white/60" htmlFor="edit-title">
            Name
          </label>
          <input
            id="edit-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            className="h-12 w-full rounded-xl border border-white/20 bg-zinc-950 px-3 text-base text-white outline-none focus:border-primary"
            data-testid="edit-title"
          />
          {!title.trim() && <div className="mt-1 text-xs text-white/60">Required field</div>}
          <label className="mb-1.5 mt-4 block text-xs font-bold uppercase tracking-wide text-white/60" htmlFor="edit-note">
            Note
          </label>
          <textarea
            id="edit-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={500}
            className="w-full resize-none rounded-xl border border-white/20 bg-zinc-950 px-3 py-3 text-base text-white outline-none focus:border-primary"
            data-testid="edit-note"
          />
          {editError && (
            <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400" role="alert" data-testid="edit-error">
              {editError}
            </div>
          )}
          <button
            type="button"
            onClick={saveEdit}
            disabled={saving || !title.trim()}
            className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-base font-bold text-primary-foreground disabled:opacity-50"
            data-testid="edit-save"
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Save
          </button>
        </Panel>
      )}
    </div>
  );
}

/* ---------- fotos de uma area ---------- */

function AreaView({ site, areaId, user, isStaff }) {
  const navigate = useNavigate();
  const [area, setArea] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [person, setPerson] = useState("");
  const [viewer, setViewer] = useState(-1);
  const [adding, setAdding] = useState(false);
  const [menu, setMenu] = useState(false);
  const [renaming, setRenaming] = useState("");
  const [menuBusy, setMenuBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/photo-areas/${areaId}/photos`, { params: { limit: 200 } });
      setArea(data.area);
      setPhotos(data.photos || []);
      setHasMore(Boolean(data.has_more));
      setError("");
    } catch (e) {
      setError(e.response?.status === 404 ? "This folder no longer exists." : formatApiError(e.response?.data?.detail, "Could not load the photos."));
      setPhotos((p) => p || []);
    }
  }, [areaId]);

  useEffect(() => {
    setPhotos(null);
    load();
  }, [load]);

  const loadMore = async () => {
    if (!photos || photos.length === 0 || loadingMore) return;
    setLoadingMore(true);
    try {
      const before = photos[photos.length - 1].created_at;
      const { data } = await api.get(`/photo-areas/${areaId}/photos`, { params: { limit: 200, before } });
      setPhotos((prev) => [...prev, ...(data.photos || [])]);
      setHasMore(Boolean(data.has_more));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not load more photos."));
    } finally {
      setLoadingMore(false);
    }
  };

  const people = useMemo(() => {
    const m = new Map();
    (photos || []).forEach((p) => m.set(p.user_id, p.user_name || "Unknown"));
    return [...m.entries()];
  }, [photos]);

  const groups = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const min = dateFilter === "today" ? today : dateFilter === "7d" ? new Date(today.getTime() - 6 * DAY) : dateFilter === "30d" ? new Date(today.getTime() - 29 * DAY) : null;
    const list = (photos || [])
      .filter((p) => (!min || new Date(p.taken_at) >= min) && (!person || p.user_id === person))
      .sort((a, b) => new Date(b.taken_at) - new Date(a.taken_at));
    const out = [];
    let offset = 0;
    list.forEach((p) => {
      const d = new Date(p.taken_at);
      const k = dayKeyOf(d);
      let g = out[out.length - 1];
      if (!g || g.key !== k) {
        g = { key: k, date: d, items: [], start: offset };
        out.push(g);
      }
      g.items.push(p);
      offset += 1;
    });
    return { groups: out, flat: list };
  }, [photos, dateFilter, person]);

  const canDelete = (p) => isStaff || (p.user_id === user.id && Date.now() - new Date(p.created_at).getTime() < DAY);

  // [photo-menu] salva o nome e a nota editados no servidor e atualiza a foto na tela
  const editPhoto = async (p, changes) => {
    const { data } = await api.patch(`/site-photos/${p.id}`, changes);
    setPhotos((prev) => prev.map((x) => (x.id === p.id ? { ...x, ...data } : x)));
    return data;
  };

  const deletePhoto = async (p) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/site-photos/${p.id}`);
      setPhotos((prev) => prev.filter((x) => x.id !== p.id));
      const left = groups.flat.length - 1;
      setViewer((i) => (left <= 0 ? -1 : Math.min(i, left - 1)));
      toast.success("Photo deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not delete the photo."));
    }
  };

  const saveRename = async () => {
    if (menuBusy || !renaming.trim()) return;
    setMenuBusy(true);
    try {
      const { data } = await api.patch(`/photo-areas/${areaId}`, { name: renaming });
      setArea((a) => ({ ...a, name: data.name }));
      setMenu(false);
      toast.success("Folder renamed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not rename the folder."));
    } finally {
      setMenuBusy(false);
    }
  };

  const deleteArea = async () => {
    const n = (photos || []).length;
    const text = n > 0 ? `Delete this folder and its ${plural(n, "photo", "photos")}? This cannot be undone.` : "Delete this folder?";
    if (menuBusy || !window.confirm(text)) return;
    setMenuBusy(true);
    try {
      await api.delete(`/photo-areas/${areaId}`, { params: { force: n > 0 } });
      toast.success("Folder deleted");
      navigate("/app/photos");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not delete the folder."));
      setMenuBusy(false);
    }
  };

  const chipClass = "h-10 max-w-[48%] appearance-none truncate rounded-full border border-border bg-card pl-3.5 pr-8 text-sm font-semibold outline-none focus:border-primary";
  const areaName = area ? area.name : "Documents";

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 md:px-6 md:pb-12 md:pt-8" data-testid="area-page">
      <TopBar
        title={areaName}
        onBack={() => navigate("/app/photos")}
        right={
          isStaff && area ? (
            <button
              type="button"
              onClick={() => {
                setRenaming(area.name);
                setMenu(true);
              }}
              aria-label="Folder options"
              className="grid h-11 w-11 place-items-center rounded-full active:bg-accent"
              data-testid="area-menu"
            >
              <MoreHorizontal className="h-6 w-6" />
            </button>
          ) : null
        }
      />

      {error && (
        <div className="mb-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400" role="alert" data-testid="area-error">
          {error}
        </div>
      )}

      {photos && photos.length > 0 && (
        <div className="mb-1 flex gap-2" data-testid="filters">
          <div className="relative">
            <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className={chipClass} aria-label="Filter by date" data-testid="filter-date">
              <option value="all">All dates</option>
              <option value="today">Today</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
          <div className="relative">
            <select value={person} onChange={(e) => setPerson(e.target.value)} className={chipClass} aria-label="Filter by person" data-testid="filter-person">
              <option value="">All people</option>
              {people.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </div>
      )}

      {photos === null ? (
        <div className="grid h-40 place-items-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : photos.length === 0 ? (
        !error && (
          <div className="mt-6 rounded-2xl border border-dashed border-border p-8 text-center" data-testid="area-empty">
            <Images className="mx-auto h-8 w-8 text-muted-foreground" />
            <div className="mt-3 text-lg font-bold">No photos yet</div>
            <div className="mt-1 text-sm text-muted-foreground">Add the first photo to this folder.</div>
          </div>
        )
      ) : groups.flat.length === 0 ? (
        <div className="mt-8 text-center text-sm text-muted-foreground" data-testid="area-nomatch">
          No photos match these filters.
        </div>
      ) : (
        groups.groups.map((g) => (
          <section key={g.key} data-testid="day-group">
            <h2 className="mb-2 mt-5 text-[13px] font-bold text-muted-foreground">
              {dayLabel(g.date)} · {plural(g.items.length, "photo", "photos")}
            </h2>
            <div className="grid grid-cols-3 gap-[3px] md:grid-cols-5">
              {g.items.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setViewer(g.start + i)}
                  className="relative aspect-square overflow-hidden bg-muted"
                  data-testid={`photo-${p.id}`}
                  aria-label={`Photo by ${p.user_name || "unknown"} at ${timeLabel(new Date(p.taken_at))}`}
                >
                  <img src={resolveFileUrl(p.thumb_url)} alt="" loading="lazy" className="h-full w-full object-cover" />
                  <span className="absolute bottom-1 left-1.5 text-[10px] font-bold text-white [text-shadow:0_1px_2px_rgba(0,0,0,.8)]">{timeLabel(new Date(p.taken_at)).replace(" ", "")}</span>
                </button>
              ))}
            </div>
          </section>
        ))
      )}

      {hasMore && photos && (
        <button type="button" onClick={loadMore} disabled={loadingMore} className="mt-5 h-12 w-full rounded-xl border border-border text-sm font-semibold active:bg-accent disabled:opacity-60" data-testid="load-more">
          {loadingMore ? "Loading..." : "Load older photos"}
        </button>
      )}

      <div className="sticky bottom-3 z-10 mt-6">
        <BigButton onClick={() => setAdding(true)} testId="add-here">
          <Camera className="h-5 w-5" /> Add photos here
        </BigButton>
      </div>

      {adding && <AddPhotosSheet site={site} initialAreaId={areaId} onClose={() => setAdding(false)} onDone={() => load()} />}

      {viewer >= 0 && groups.flat.length > 0 && (
        <Viewer photos={groups.flat} index={Math.min(viewer, groups.flat.length - 1)} areaName={areaName} onIndex={setViewer} onClose={() => setViewer(-1)} canDelete={canDelete} onDelete={deletePhoto} canEdit={canDelete} onEdit={editPhoto} />
      )}

      {menu && area && (
        <Sheet title="Folder options" subtitle={area.name} onClose={() => setMenu(false)}>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted-foreground" htmlFor="rename">
            Name
          </label>
          <div className="flex gap-2">
            <input
              id="rename"
              value={renaming}
              onChange={(e) => setRenaming(e.target.value)}
              maxLength={80}
              className="h-12 min-w-0 flex-1 rounded-xl border border-input bg-background px-3 text-base outline-none focus:border-primary"
              data-testid="rename-input"
            />
            <button type="button" onClick={saveRename} disabled={menuBusy || !renaming.trim()} className="flex h-12 items-center gap-1.5 rounded-xl bg-primary px-4 font-bold text-primary-foreground disabled:opacity-50" data-testid="rename-save">
              <Pencil className="h-4 w-4" /> Save
            </button>
          </div>
          <button type="button" onClick={deleteArea} disabled={menuBusy || ((photos || []).length > 0 && user.role !== "admin" && !user.is_master_admin)} className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-red-500/50 font-semibold text-red-400 active:bg-red-500/10 disabled:opacity-40" data-testid="delete-area">
            <Trash2 className="h-4 w-4" /> Delete folder
          </button>
          {(photos || []).length > 0 && user.role !== "admin" && !user.is_master_admin && <p className="mt-2 text-xs text-muted-foreground">Only an admin can delete a folder that has photos.</p>}
        </Sheet>
      )}
    </div>
  );
}

/* ---------- pagina ---------- */

export default function Photos() {
  const { areaId } = useParams();
  const { user } = useAuth();
  const { activeSite } = useJobsite();
  const navigate = useNavigate();
  const isStaff = user?.role === "admin" || user?.role === "supervisor" || Boolean(user?.is_master_admin);

  if (!activeSite) {
    return (
      <div className="mx-auto max-w-3xl px-4 pt-4 md:px-6 md:pt-8" data-testid="photos-nosite">
        <TopBar title="Documents" onBack={() => navigate("/app")} />
        <div className="mt-8 rounded-2xl border border-border bg-card p-6 text-center">
          <Images className="mx-auto h-8 w-8 text-muted-foreground" />
          <div className="mt-3 text-lg font-bold">No project selected</div>
          <div className="mt-1 text-sm text-muted-foreground">Choose a project in the bar at the top of the screen.</div>
        </div>
      </div>
    );
  }
  return areaId ? <AreaView key={areaId} site={activeSite} areaId={areaId} user={user} isStaff={isStaff} /> : <AreasView key={activeSite.id} site={activeSite} isStaff={isStaff} />;
}
